import { Box, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { tokens } from "../../theme";
import { useFormat, type Formatters } from "../../i18n/format";
import { formatMoney } from "./offerRevenueFormat";
import type { OfferFunnelDay } from "@shared/types/offerFunnel";

/**
 * The window, day by day — three small charts rather than one clever one.
 *
 * ## Why three, and never one with two y-axes
 *
 * Money, impressions and requests are three measures of wildly different
 * magnitude: a thousand impressions, forty requests and 12 400 kr cannot share
 * a scale without one of them becoming a flat line along the floor. The usual
 * fix is a second y-axis, which is the single most misread thing in business
 * charting — two scales let a reader see a crossing that is an artefact of
 * where the axes were pinned. So: small multiples, one measure each, one axis
 * each, the same x for all three.
 *
 * ## Why money is drawn apart from attention
 *
 * The same reason the funnel draws a rule down its middle. Impressions are a
 * per-day count with no room and no guest; revenue is a sum of individually
 * priced rows. Side by side over a window is the honest comparison. Overlaid
 * on one baseline they would read as a causal series, which is precisely the
 * precision this data does not have (`docs/room-offers.md` §5).
 *
 * ## Pipeline is not plotted
 *
 * Deliberately. A pending request has earned nothing, and a dotted line above
 * the revenue series is exactly how "we nearly made this" becomes "we made
 * this" in a slide three weeks later. It is a labelled figure on the page, and
 * that is where it stays.
 */

/** Below this many days a column per day still has room to breathe. */
const COLUMN_LIMIT = 62;

const W = 320;
const H = 84;
/** Room under the plot for the day labels. */
const PAD_B = 14;
const PLOT_H = H - PAD_B;

interface Point {
  day: string;
  /** The measure, already reduced to one number in one unit. */
  value: number;
  /** For the stacked chart: the part of `value` that counts as money. */
  part?: number;
}

/** `2026-08-14` → `14 Aug`. Short, because it sits under a 320px plot. */
function shortDay(fmt: Formatters, day: string): string {
  const d = new Date(`${day}T00:00:00Z`);
  if (Number.isNaN(d.getTime())) return day;
  return fmt.date(d, { day: "numeric", month: "short", timeZone: "UTC" });
}

/**
 * One day's revenue in ONE currency.
 *
 * The chart plots the currency the headline is expressed in and says so in its
 * subtitle. Summing a day's currencies into one bar height would be adding
 * kronor to euros to decide a geometry, which is the same mistake as adding
 * them to print a total.
 */
export function revenueIn(day: OfferFunnelDay, currency: string): number {
  return day.revenue.find((m) => m.currency === currency)?.amount ?? 0;
}

/** A clean top tick: the smallest 1/2/5×10ⁿ at or above the peak. */
export function niceCeiling(max: number): number {
  if (max <= 0) return 1;
  const magnitude = 10 ** Math.floor(Math.log10(max));
  for (const step of [1, 2, 5, 10]) {
    if (max <= step * magnitude) return step * magnitude;
  }
  return 10 * magnitude;
}

interface TrendChartProps {
  title: string;
  subtitle?: string;
  points: Point[];
  colour: string;
  /** How to say a value in a tooltip and in the end label. */
  format: (value: number) => string;
  /** Draws `part` solid and the remainder as a pale track of the same hue. */
  stacked?: boolean;
  testId: string;
}

/**
 * One measure over the window.
 *
 * Columns while a day still has room for one; a line the moment it does not,
 * because 400 sub-pixel columns are a texture rather than a chart. The last
 * value is direct-labelled and every mark carries a `<title>`, so the numbers
 * the axis rounds away are still one hover from the reader — and the table
 * below the charts carries all of them regardless.
 */
export function TrendChart({
  title,
  subtitle,
  points,
  colour,
  format,
  stacked = false,
  testId,
}: TrendChartProps) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  const max = niceCeiling(Math.max(0, ...points.map((p) => p.value)));
  const n = points.length;
  const asColumns = n > 0 && n <= COLUMN_LIMIT;
  const slot = n > 0 ? W / n : W;
  // Cap the bar and leave the slot's remainder as air; 2px of that air is the
  // surface gap that separates touching columns.
  const barW = Math.max(1, Math.min(24, slot - 2));
  const y = (v: number) => PLOT_H - (v / max) * PLOT_H;
  const last = points[n - 1];

  return (
    <Box sx={{ flex: "1 1 260px", minWidth: 240 }} data-testid={testId}>
      <Typography
        variant="bodySm"
        sx={{ fontWeight: "fontWeightSemibold", color: tokens.ink }}
      >
        {title}
      </Typography>
      <Typography
        variant="micro"
        sx={{ color: tokens.inkFaint, minHeight: 16 }}
        data-testid={`${testId}-subtitle`}
      >
        {subtitle ?? ""}
      </Typography>
      <Stack direction="row" spacing={1} alignItems="flex-start" sx={{ mt: 1 }}>
        <Typography
          variant="micro"
          sx={{
            color: tokens.inkFaint,
            fontVariantNumeric: "tabular-nums",
            width: 52,
            textAlign: "right",
            pt: "1px",
          }}
        >
          {format(max)}
        </Typography>
        <Box
          component="svg"
          viewBox={`0 0 ${W} ${H}`}
          role="img"
          aria-label={t("offerRevenueTrend.peak", {
            title,
            value: format(max),
          })}
          sx={{ width: "100%", height: "auto", display: "block" }}
        >
          {/* Baseline and top rule: hairline, solid, one step off the surface. */}
          <line
            x1={0}
            y1={PLOT_H}
            x2={W}
            y2={PLOT_H}
            stroke={tokens.divider}
            strokeWidth={1}
          />
          <line
            x1={0}
            y1={0.5}
            x2={W}
            y2={0.5}
            stroke={tokens.divider}
            strokeWidth={1}
          />

          {asColumns
            ? points.map((p, i) => {
                const x = i * slot + (slot - barW) / 2;
                const top = y(p.value);
                const partTop = stacked ? y(p.part ?? 0) : top;
                return (
                  <g key={p.day}>
                    <title>
                      {t("offerRevenueTrend.point", {
                        day: shortDay(fmt, p.day),
                        value: format(p.value),
                      })}
                    </title>
                    {p.value > 0 && (
                      <rect
                        x={x}
                        y={top}
                        width={barW}
                        height={Math.max(1, PLOT_H - top)}
                        rx={2}
                        fill={stacked ? tokens.rowAlt : colour}
                      />
                    )}
                    {stacked && (p.part ?? 0) > 0 && (
                      <rect
                        x={x}
                        y={partTop}
                        width={barW}
                        height={Math.max(1, PLOT_H - partTop)}
                        rx={2}
                        fill={colour}
                      />
                    )}
                  </g>
                );
              })
            : (() => {
                const path = points
                  .map(
                    (p, i) =>
                      `${i === 0 ? "M" : "L"}${(i * W) / Math.max(1, n - 1)},${y(p.value)}`
                  )
                  .join(" ");
                const area = `${path} L${W},${PLOT_H} L0,${PLOT_H} Z`;
                return (
                  <g>
                    <path d={area} fill={colour} opacity={0.1} />
                    <path
                      d={path}
                      fill="none"
                      stroke={colour}
                      strokeWidth={2}
                      strokeLinejoin="round"
                      strokeLinecap="round"
                    />
                    {last && (
                      <circle
                        cx={W}
                        cy={y(last.value)}
                        r={4}
                        fill={colour}
                        stroke={tokens.surface}
                        strokeWidth={2}
                      />
                    )}
                  </g>
                );
              })()}

          {points[0] && (
            <text
              x={0}
              y={H - 3}
              fontSize={9}
              fill={tokens.inkFaint}
              textAnchor="start"
            >
              {shortDay(fmt, points[0].day)}
            </text>
          )}
          {last && n > 1 && (
            <text
              x={W}
              y={H - 3}
              fontSize={9}
              fill={tokens.inkFaint}
              textAnchor="end"
            >
              {shortDay(fmt, last.day)}
            </text>
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export interface OfferRevenueTrendProps {
  series: OfferFunnelDay[];
  /** The currency the headline is in; the revenue chart plots that one. */
  currency: string | null;
}

export default function OfferRevenueTrend({
  series,
  currency,
}: OfferRevenueTrendProps) {
  const { t } = useTranslation("offers");
  const fmt = useFormat();
  const money = (v: number) =>
    currency ? formatMoney(fmt, { amount: v, currency }) : fmt.number(v);
  const count = (v: number) => fmt.number(v);

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={3} flexWrap="wrap" useFlexGap>
        <TrendChart
          testId="trend-revenue"
          title={t("offerRevenueTrend.revenueTitle")}
          subtitle={
            currency
              ? t("offerRevenueTrend.revenueSubtitle", { currency })
              : t("offerRevenueTrend.revenueEmpty")
          }
          points={series.map((d) => ({
            day: d.day,
            value: currency ? revenueIn(d, currency) : 0,
          }))}
          colour={tokens.vizMoney}
          format={money}
        />
        <TrendChart
          testId="trend-impressions"
          title={t("offerRevenueTrend.impressionsTitle")}
          subtitle={t("offerRevenueTrend.impressionsSubtitle")}
          points={series.map((d) => ({ day: d.day, value: d.impressions }))}
          colour={tokens.vizAttention}
          format={count}
        />
        <TrendChart
          testId="trend-requests"
          title={t("offerRevenueTrend.requestsTitle")}
          subtitle={t("offerRevenueTrend.requestsSubtitle")}
          points={series.map((d) => ({
            day: d.day,
            value: d.requests,
            part: d.accepted,
          }))}
          colour={tokens.vizMoney}
          format={count}
          stacked
        />
      </Stack>
    </Stack>
  );
}
